import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CircleAlert, CircleCheck, FileText, Mail, Paperclip } from 'lucide-react';
import { Card } from './Card';
import ActionButton from './ActionButton';
import Toggle from './Toggle';
import EmailCountdown from './EmailCountdown';
import { formatEmailSentAt } from './medicalApprovalEmail';
import { getApplicableDocuments } from '../../../helper';

const Status = ({ done, doneLabel = 'Signed' }) => (
    <span className={`tw-text-sm tw-font-medium ${done ? 'tw-text-green-700' : 'tw-text-red-600'}`}>
        {done ? doneLabel : 'Missing'}
    </span>
);

const DocumentRow = ({ label, done, children, footer }) => (
    <li className={`tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3 ${done ? '' : 'tw-bg-red-50'}`}>
        {done
            ? <CircleCheck className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-neutral-500" aria-hidden="true" />
            : <CircleAlert className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-red-600" aria-hidden="true" />}
        <span className={`tw-min-w-0 tw-flex-1 tw-text-sm ${done ? 'tw-text-neutral-800' : 'tw-font-medium tw-text-red-700'}`}>{label}</span>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">{children}</div>
        {/* Own full-width line, lined up with the label (past the icon). */}
        {footer ? <div className="tw-basis-full tw-pl-7">{footer}</div> : null}
    </li>
);

// Same look as ActionButton's primary variant. A <label> wrapping a hidden file input, so the
// click opens the file picker without a script.
const UploadButton = ({ onChange, children }) => (
    <label className="tw-m-0 tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-md tw-bg-brand-600 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-brand-700 focus-within:tw-ring-2 focus-within:tw-ring-brand-300">
        <Paperclip className="tw-h-4 tw-w-4" aria-hidden="true" />
        {children}
        <input type="file" onChange={onChange} className="tw-sr-only" />
    </label>
);

const FileLink = ({ href, children }) => (
    <li>
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-brand-600 hover:tw-text-brand-700 hover:tw-underline"
        >
            <FileText className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden="true" />
            {children}
        </a>
    </li>
);

/**
 * How many rows of the Documents card are Missing. Shared with the section nav's badge so the two
 * always agree. Only forms the program's sliders switch on count (`getApplicableDocuments`). The
 * API's `getMissingDocuments` (bookingFacade.js, "Send reminder") applies the same rules: change
 * both together.
 */
export const countMissingDocuments = (camper, isDisclaimer, program) => {
    const applies = getApplicableDocuments(program, camper);
    return [
        applies.keyDeposit && !camper?.keyDeposit,
        applies.concussion && !camper?.concussion,
        applies.medicalClearance && !(camper?.medicalDocument || []).some(url => url && url.length > 1),
        !isDisclaimer,
    ].filter(Boolean).length;
};

/**
 * Documents & Waivers: the booking's forms with a Signed / Missing status, the medical clearance
 * upload with its Approved toggle, and the files uploaded so far.
 *
 * Status only: signed dates need API work (phase 2, A3).
 * - "Send reminder" (header) shows only while something is Missing; `onSendReminder` emails the
 *   guardian the missing list (`booking/sendDocumentReminder`, at most once a day per camper).
 * - `onOpenForm(type, signed)` opens the existing PDF modal: read-only when signed, with "I Agree"
 *   when not.
 * - Rows follow the program's Super Admin sliders (`program`): a form switched off is hidden and
 *   never Missing. Key Deposit is also hidden for commuters, as before. Uploaded files always show.
 * - Waiver Disclaimer opens the same modal with the parent side's PDF. Its flag is booking-wide
 *   (`isDisclaimer`), so signing marks it for every participant on the booking; it can't be undone.
 *   Director-created bookings start without it (parent checkout sets it).
 * - While medical clearance is approved, a line under its row shows the approval email to the
 *   parent (`medicalEmail`, see medicalApprovalEmail.js): a countdown while it can still be undone,
 *   when it was sent, or that there's no parent email. `onMedicalEmailElapsed` runs when the
 *   countdown hits zero.
 */
const DocumentsCard = ({ camper, program, isDisclaimer, medicalApproved, onToggleApproval, onOpenForm, onUploadMedical, onSendReminder, sendingReminder, medicalEmail, onMedicalEmailElapsed }) => {
    const medicalDocuments = (camper?.medicalDocument || []).filter(url => url && url.length > 1);
    const hasMedical = medicalDocuments.length > 0;
    const applies = getApplicableDocuments(program, camper);

    const files = [
        camper?.gardianPdf && { href: camper.gardianPdf, label: 'PDF Uploaded' },
        camper?.hippaDocument && { href: camper.hippaDocument, label: 'Hippa Document' },
        // Newest first, keeping each document's upload number.
        ...medicalDocuments.map((href, i) => ({ href, label: `Medical Document ${i + 1}` })).reverse(),
        camper?.waiver && { href: camper.waiver, label: 'TCL Waiver' },
        camper?.waiverLiability && { href: camper.waiverLiability, label: 'Waiver of Liability for COVID-19 Student Sport or Activity' },
    ].filter(Boolean);

    const formRow = (type, label) => (
        <DocumentRow label={label} done={!!camper?.[type]}>
            <Status done={!!camper?.[type]} />
            <ActionButton onClick={() => onOpenForm(type, !!camper?.[type])}>{camper?.[type] ? 'View' : 'View & Sign'}</ActionButton>
        </DocumentRow>
    );

    let medicalEmailLine = null;
    if (medicalApproved && medicalEmail) {
        if (medicalEmail.sendAt) {
            medicalEmailLine = <EmailCountdown sendAt={medicalEmail.sendAt} clockOffset={medicalEmail.clockOffset} onElapsed={onMedicalEmailElapsed} />;
        } else if (medicalEmail.sentAt) {
            medicalEmailLine = (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-neutral-500">
                    <Mail className="tw-h-3.5 tw-w-3.5 tw-shrink-0" aria-hidden="true" />
                    Approval email sent {formatEmailSentAt(medicalEmail.sentAt)}
                </span>
            );
        } else if (medicalEmail.noGuardianEmail) {
            medicalEmailLine = <span className="tw-text-xs tw-text-neutral-500">No parent email on file — approval email won't be sent</span>;
        }
    }

    const reminderButton =onSendReminder && countMissingDocuments(camper, isDisclaimer, program) > 0
        ? <ActionButton variant="accent" size="sm" onClick={onSendReminder} disabled={sendingReminder}>{sendingReminder ? 'Sending…' : 'Send reminder'}</ActionButton>
        : null;

    return (
        <Card id="documents" title="Documents & Waivers" icon={FileText} actions={reminderButton} className="tw-overflow-hidden">
            {/* Rows run edge to edge, so undo the card body's padding. */}
            <ul className="-tw-mx-5 -tw-mb-4">
                {applies.keyDeposit ? formRow('keyDeposit', 'Key Deposit') : null}
                {applies.concussion ? formRow('concussion', 'Concussion Waiver') : null}
                {applies.medicalClearance ? <DocumentRow label="Medical Clearance Form & Physical" done={hasMedical} footer={medicalEmailLine}>
                    <Status done={hasMedical} doneLabel="Uploaded" />
                    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-600">
                        {/* The status lives on the participant, so it also shows on their other bookings. */}
                        {medicalApproved ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="medical-approval-shared">
                                    This approval is shared by all of this participant's bookings. Approving it on any one of them approves it on all.
                                </Tooltip>}>
                                <CircleAlert className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-amber-500" aria-label="Medical approval is shared across bookings" />
                            </OverlayTrigger>
                        ) : null}
                        Approved
                        <Toggle checked={medicalApproved} onChange={onToggleApproval} label="Medical clearance approved" />
                    </span>
                    <UploadButton onChange={onUploadMedical}>{hasMedical ? 'Add More' : 'Attach PDF'}</UploadButton>
                </DocumentRow> : null}
                <DocumentRow label="Waiver Disclaimer" done={!!isDisclaimer}>
                    <Status done={!!isDisclaimer} />
                    <ActionButton onClick={() => onOpenForm('isDisclaimer', !!isDisclaimer)}>{isDisclaimer ? 'View' : 'View & Sign'}</ActionButton>
                </DocumentRow>
                <li className="tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3">
                    <div className="tw-mb-2 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-400">Uploaded files</div>
                    {files.length
                        ? <ul className="tw-flex tw-flex-col tw-gap-1.5">
                            {files.map(f => <FileLink key={f.href + f.label} href={f.href}>{f.label}</FileLink>)}
                        </ul>
                        : <p className="tw-text-sm tw-italic tw-text-neutral-400">No files uploaded.</p>}
                </li>
            </ul>
        </Card>
    );
};

export default DocumentsCard;
